// AuthLayout.js - Updated with improved language handling
import React, { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { Menu as MenuIcon } from "lucide-react";
import logo from "assets/icons/logo.svg";
import useLanguage from "hooks/useLanguage"; // Import the custom hook

const AuthLayout = ({ children }: { children: ReactNode }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Use the custom language hook
  const { t, toggleLanguage, alternativeLanguage, alternativeLanguageLabel } =
    useLanguage();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="px-4 md:px-8 py-4 flex justify-between items-center relative">
        <div
          className="w-[120px] h-8"
          style={{
            backgroundImage: `url(${logo})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        ></div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex gap-4">
          <Link
            to="/"
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            {t("login")}
          </Link>
          <Link
            to="/merchant-registration"
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            {t("registration")}
          </Link>
          <span
            onClick={toggleLanguage}
            className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer flex items-center gap-1"
            title={`Switch to ${alternativeLanguageLabel}`}
          >
            <span className="text-sm">{alternativeLanguage}</span>
          </span>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden">
          <button onClick={toggleMobileMenu} className="p-2 focus:outline-none">
            <MenuIcon />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-white shadow-md md:hidden z-10">
            <div className="flex flex-col items-center py-4 gap-4">
              <Link
                to="/"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("login")}
              </Link>
              <Link
                to="/merchant-registration"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("registration")}
              </Link>
              <span
                onClick={() => {
                  toggleLanguage();
                  setIsMobileMenuOpen(false);
                }}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
                title={`Switch to ${alternativeLanguageLabel}`}
              >
                {alternativeLanguage}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div
        className="overflow-y-scroll h-full max-w-full bg-gray-200 scroll-smooth 
        px-4 md:px-[5rem] lg:px-[15rem]"
        onClick={() => setIsMobileMenuOpen(false)}
      >
        {children}
      </div>
    </div>
  );
};

export default AuthLayout;
